/**
 * The safety check. A move is safe when, after making it, some short continuation keeps Digby
 * alive to the horizon and leaves him with nothing falling onto him, no creature beside him and
 * room to move (`room.ts`). It is good when, besides, he has not been walled off from the exit or
 * from the diamonds he needs (`prospects.ts`). The continuation that proved it is kept by the
 * pilot as its escape plan: the world is deterministic, so following it stays safe while no
 * better move can be proved.
 */

import type { CaveState, Input } from '../sim';
import { STAY } from './inputs';
import { Flood, NO_EXIT, worse } from './prospects';
import type { Prospects } from './prospects';
import { imperiled, Room } from './room';
import { Sandbox } from './sandbox';

/** Scans of look-ahead, the first move included; `LONG_HORIZON` with a creature close by. */
export const HORIZON = 6;
export const LONG_HORIZON = 9;

export interface Verdict {
  /** Survivable and nothing lost. */
  ok: boolean;
  /** Survivable, though maybe walled off from the exit or the diamonds. */
  safe: boolean;
  /** Scans the best continuation tried survived. */
  survived: number;
  /** When safe: the inputs after the first that were proved safe (one fewer than the horizon). */
  plan: Input[];
}

const FAIL: Verdict = { ok: false, safe: false, survived: 0, plan: [] };

export class Verifier {
  private readonly base: Sandbox;
  private readonly work: Sandbox;
  private readonly flood: Flood;
  private readonly room: Room;
  /** Scans the checks may simulate before `resetBudget` is called again. */
  private budget = 0;
  private readonly after: Prospects = { exit: NO_EXIT, diamonds: 0, reachable: 0 };

  constructor(like: CaveState) {
    const n = like.w * like.h;
    this.base = new Sandbox(like);
    this.work = new Sandbox(like);
    this.flood = new Flood(n);
    this.room = new Room(n);
  }

  resetBudget(scans: number): void {
    this.budget = scans;
    this.base.steps = 0;
    this.work.steps = 0;
  }

  get exhausted(): boolean {
    return this.base.steps + this.work.steps >= this.budget;
  }

  /** What Digby can get to in `s` right now. */
  prospects(s: CaveState, out: Prospects): Prospects {
    return this.flood.measure(s, out);
  }

  /**
   * Make `first` in a copy of `s`, then try each continuation in turn until one keeps Digby safe
   * to the horizon without losing `before`. A continuation shorter than the horizon is padded by
   * standing still.
   */
  check(
    s: CaveState,
    first: Input,
    continuations: readonly (readonly Input[])[],
    before: Prospects,
    horizon = HORIZON,
  ): Verdict {
    if (this.exhausted) return FAIL;
    const base = this.base;
    base.load(s);
    base.run(first);
    if (base.dead()) return FAIL;
    if (base.won()) return { ok: true, safe: true, survived: horizon, plan: [] };
    let best = 1;
    let safePlan: Input[] | null = null;
    for (const cont of continuations) {
      if (this.exhausted) break;
      const work = this.work;
      work.load(base.state);
      let k = 1;
      for (; k < horizon; k++) {
        work.run(cont[k - 1] ?? STAY);
        if (work.dead()) break;
        if (work.won()) return { ok: true, safe: true, survived: horizon, plan: planOf(cont, k) };
      }
      if (k > best) best = k;
      if (k < horizon || imperiled(work.state) || !this.room.free(work.state)) continue;
      const lost = worse(before, this.flood.measure(work.state, this.after), s.params.diamondsNeeded);
      if (!lost) return { ok: true, safe: true, survived: horizon, plan: planOf(cont, horizon - 1) };
      safePlan ??= planOf(cont, horizon - 1);
    }
    if (safePlan !== null) return { ok: false, safe: true, survived: horizon, plan: safePlan };
    return { ok: false, safe: false, survived: best, plan: [] };
  }

  /**
   * Make `input`, then follow the first of `escapes` that lets Digby live through `scans`: if
   * something blows up or goes through a magic wall, how many more diamonds could be reached than
   * `before`, and by which escape? `moved`: Digby moved on the first scan (a push that took).
   * Standing still is tried first; the escapes only when there is a blast to get away from.
   */
  drop(
    s: CaveState,
    input: Input,
    scans: number,
    before: Prospects,
    escapes: readonly (readonly Input[])[],
  ): { moved: boolean; gain: number; plan: Input[] } {
    const base = this.base;
    base.load(s);
    base.run(input);
    const moved = base.state.digby.moving;
    const none = { moved, gain: 0, plan: [] };
    if (base.dead()) return none;
    for (let e = -1; e < escapes.length && !this.exhausted; e++) {
      const cont = escapes[e] ?? [];
      const t = this.work;
      t.load(base.state);
      let blast = false;
      for (let k = 0; k < scans && !t.dead(); k++) {
        t.run(cont[k] ?? STAY);
        for (const ev of t.state.events) if (ev.type === 'explode' || ev.type === 'magicConvert') blast = true;
      }
      if (!blast) return none;
      if (t.dead()) continue;
      const gain = this.flood.measure(t.state, this.after).reachable - before.reachable;
      return { moved, gain, plan: planOf(cont, HORIZON - 1) };
    }
    return none;
  }
}

function planOf(cont: readonly Input[], length: number): Input[] {
  const plan: Input[] = [];
  for (let i = 0; i < length; i++) plan.push(cont[i] ?? STAY);
  return plan;
}
